import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import ImageInput from 'components/field/ImageInput';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Icon from 'components/global/Icon';
import Modal from 'components/global/Modal';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { DELIVERY_COMPANIES, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import * as S from './DeliveryCompanyForm.styled';

const DeliveryCompanyForm = NiceModal.create(({ id: ID, modalName, mode }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();
	const defaultColSpan = {
		md: 6
	};
	const viewMode = useMemo(() => mode === 'view', [mode]);
	const { t } = useTranslation();

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					grayScaleLogo: yup.string().nullable(),
					statusIntegrationName: yup.string().nullable(),
					companyName: yup.string().nullable().required(),
					companyNameAr: yup.string().nullable().required(),
					connectionNeeded: yup.boolean().nullable().required(),
					icon: yup.string().nullable(),
					integrationName: yup.string().nullable().required(),
					website: yup.string().nullable().required(),
					companyType: yup.string().nullable(),
					codAvailable: yup.boolean().nullable().required(),
					codType: yup.string().nullable(),
					rating: yup.number().nullable(),
					status: yup.string().nullable(),
					paymentTerm: yup.string().nullable(),
					maxCheckStatusLimit: yup.number().nullable(),
					maxCheckStatusRequestLimit: yup.number().nullable(),
					codTransfer: yup.string().nullable(),
					webhookAvailable: yup.boolean().nullable().required(),
					testConnectionAvailable: yup.boolean().nullable().required(),
					deliveryGatewayImplemented: yup.boolean().nullable().required(),
					printAWBNA: yup.boolean().nullable().required(),
					trackingURL: yup.string().nullable(),
					description: yup.string().nullable(),
					jsonSettings: yup.string().nullable().json(),
					countries: yup.array().nullable(),
					serviceTypes: yup.array().nullable(),
					deliveryCompanySettingsConfig: yup.array().of(
						yup.object().shape({
							parentJSON: yup.string().nullable(),
							fieldName: yup.string().nullable(),
							fieldType: yup.string().nullable(),
							enumValues: yup.string().nullable(),
							defaultValue: yup.string().nullable(),
							isRequired: yup.boolean().nullable().required(),
							isStatic: yup.boolean().nullable().required()
						})
					)
				})
				.required(),
		[]
	);

	const fillForm = useCallback(async () => {
		if (!ID) {
			return {
				grayScaleLogo: null,
				statusIntegrationName: null,
				companyName: null,
				companyNameAr: null,
				connectionNeeded: false,
				icon: null,
				integrationName: null,
				website: null,
				companyType: null,
				codAvailable: false,
				codType: null,
				rating: null,
				status: null,
				paymentTerm: null,
				maxCheckStatusLimit: null,
				maxCheckStatusRequestLimit: null,
				codTransfer: null,
				webhookAvailable: false,
				testConnectionAvailable: false,
				deliveryGatewayImplemented: false,
				printAWBNA: false,
				trackingURL: null,
				description: null,
				jsonSettings: null,
				countries: null,
				serviceTypes: null,
				deliveryCompanySettingsConfig: []
			};
		}

		try {
			const response = await axiosPrivate.post(DELIVERY_COMPANIES.GET_DELIVERY_COMPANY, { ID });
			const { data, status } = response;

			if (status !== 200) {
				showError();
				return;
			}
			return {
				grayScaleLogo: data.grayScaleLogo ?? null,
				statusIntegrationName: data.statusIntegrationName ?? null,
				companyName: data.companyName ?? null,
				companyNameAr: data.companyNameAr ?? null,
				connectionNeeded: data.connectionNeeded ?? null,
				icon: data.icon ?? null,
				integrationName: data.integrationName ?? null,
				website: data.website ?? null,
				companyType: data.companyType ?? null,
				codAvailable: data.codAvailable ?? null,
				codType: data.codType ?? null,
				rating: data.rating ?? null,
				status: data.status ?? null,
				paymentTerm: data.paymentTerm ?? null,
				maxCheckStatusLimit: data.maxCheckStatusLimit ?? null,
				maxCheckStatusRequestLimit: data.maxCheckStatusRequestLimit ?? null,
				codTransfer: data.codTransfer ?? null,
				webhookAvailable: data.webhookAvailable ?? null,
				testConnectionAvailable: data.testConnectionAvailable ?? null,
				deliveryGatewayImplemented: data.deliveryGatewayImplemented ?? null,
				printAWBNA: data.printAWBNA ?? null,
				trackingURL: data.trackingURL ?? null,
				description: data.description ?? null,
				jsonSettings: data.jsonSettings ?? null,
				countries: data.countries ?? null,
				serviceTypes: data.serviceTypes ?? null,

				deliveryCompanySettingsConfig: data.deliveryCompanySettingsConfig ?? []
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, [ID]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		watch,
		setValue,
		control,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const deliveryCompanySettingsConfigWatcher = useWatch({ control, name: 'deliveryCompanySettingsConfig' });

	const {
		fields: deliveryCompanySettingsConfigFields,
		append: appendDeliveryCompanySettingsConfig,
		remove: removeDeliveryCompanySettings
	} = useFieldArray({
		control,
		name: 'deliveryCompanySettingsConfig'
	});

	const deliveryCompanySettingsConfigControlledFields = deliveryCompanySettingsConfigFields?.map((field, index) => ({
		...field,
		...deliveryCompanySettingsConfigWatcher[index]
	}));

	const handleAddDeliveryCompanySettings = useCallback(() => {
		appendDeliveryCompanySettingsConfig({
			parentJSON: null,
			fieldName: null,
			fieldType: null,
			enumValues: null,
			defaultValue: null,
			isRequired: false,
			isStatic: false
		});
	}, []);

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const removeDeliveryCompanySettingsConfigItem = async (item, ID) => {
		if (!item.original.ID) {
			removeDeliveryCompanySettings(item.index);
			return;
		}
		try {
			const response = await axiosPrivate.post(DELIVERY_COMPANIES.DELETE_DELIVERY_COMPANY_SETTINGS_CONFIG_ITEM, {
				deliveryCompanyID: ID,
				ID: item.original.ID
			});
			if (response.status === 200) {
				showSuccess();
				removeDeliveryCompanySettings(item.index);
			} else {
				showError();
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const onSubmit = (data) => {
		const url = ID ? DELIVERY_COMPANIES.UPDATE_DELIVERY_COMPANY : DELIVERY_COMPANIES.ADD_DELIVERY_COMPANY;

		axiosPrivate
			.post(url, {
				...(ID && { ID }),
				...data
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.DELIVERY_COMPANIES));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({ message: error?.response?.data?.message });
			});
	};

	const deliveryCompanySettingsConfigColumns = useMemo(
		() => [
			{
				id: 'parentJSON',
				header: 'parentJSON',
				size: 120,
				renderCell: ({ row }) => (
					<StringInput name={`deliveryCompanySettingsConfig[${row.index}].parentJSON`} viewMode={viewMode} />
				)
			},
			{
				header: 'fieldName',
				accessorKey: 'fieldName',
				size: 120,
				renderCell: ({ row }) => (
					<StringInput name={`deliveryCompanySettingsConfig[${row.index}].fieldName`} viewMode={viewMode} />
				)
			},
			{
				header: 'fieldType',
				accessorKey: 'fieldType',
				size: 120,
				renderCell: ({ row }) => (
					<EnumSelect
						name={`deliveryCompanySettingsConfig[${row.index}].fieldType`}
						value={watch(`deliveryCompanySettingsConfig[${row.index}].fieldType`)}
						setValue={(value) =>
							setValue(`deliveryCompanySettingsConfig[${row.index}].fieldType`, value, {
								shouldDirty: true,
								shouldValidate: true
							})
						}
						enumName={ENUMS.FIELD_TYPES}
						viewMode={viewMode}
						multipleSelection={false}
					/>
				)
			},
			{
				header: 'enumValues',
				accessorKey: 'enumValues',
				size: 100,
				renderCell: ({ row }) => (
					<StringInput name={`deliveryCompanySettingsConfig[${row.index}].enumValues`} viewMode={viewMode} />
				)
			},
			{
				header: 'defaultValue',
				accessorKey: 'defaultValue',
				size: 100,
				renderCell: ({ row }) => (
					<StringInput
						name={`deliveryCompanySettingsConfig[${row.index}].defaultValue`}
						viewMode={viewMode}
					/>
				)
			},
			{
				header: 'isRequired',
				accessorKey: 'isRequired',
				size: 70,
				renderCell: ({ row }) => (
					<div>
						<SwitchInput
							name={`deliveryCompanySettingsConfig[${row.index}].isRequired`}
							viewMode={viewMode}
						/>
					</div>
				)
			},
			{
				header: 'isStatic',
				accessorKey: 'isStatic',
				size: 70,
				renderCell: ({ row }) => (
					<div>
						<SwitchInput
							name={`deliveryCompanySettingsConfig[${row.index}].isStatic`}
							viewMode={viewMode}
						/>
					</div>
				)
			},
			...(mode === 'edit' || mode === 'add'
				? [
						{
							id: 'deleteRow',
							size: 50,
							showOnMobile: true,
							enableHiding: false
						}
					]
				: [])
		],
		[]
	);

	return (
		<Modal fullwidth name={modalName} maxWidth="large">
			<ModalTitle>
				<Typography variant="p1">deliveryCompanyForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem col={6} label="deliveryCompanyName" isLoading={isLoading} required>
						<StringInput name="companyName" disabled={viewMode} />
					</FormItem>
					<FormItem col={6} label="companyNameAr" isLoading={isLoading} required>
						<StringInput name="companyNameAr" disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="logo" isLoading={isLoading}>
						<ImageInput
							name="icon"
							value={watch('icon')}
							disabled={viewMode}
							viewMode={viewMode}
							setValue={(value) => {
								setValue('icon', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="greyScaleLogo" isLoading={isLoading}>
						<S.GrayImage>
							<ImageInput
								name="grayScaleLogo"
								value={watch('grayScaleLogo')}
								disabled={viewMode}
								viewMode={viewMode}
								setValue={(value) => {
									setValue('grayScaleLogo', value, { shouldValidate: true, shouldDirty: true });
								}}
							/>
						</S.GrayImage>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="connectionNeeded" isLoading={isLoading}>
						<SwitchInput className="mt-3" name="connectionNeeded" viewMode={viewMode} />
					</FormItem>
					<FormItem col={6} label="integrationName" isLoading={isLoading} required>
						<StringInput name="integrationName" disabled={viewMode} />
					</FormItem>
					<FormItem col={6} label="statusIntegrationName" isLoading={isLoading}>
						<StringInput name="statusIntegrationName" disabled={viewMode} />
					</FormItem>
					<FormItem col={6} label="website" isLoading={isLoading} required>
						<StringInput name="website" disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="companyType" isLoading={isLoading}>
						<EnumSelect
							name="companyType"
							enumName={ENUMS.COMPANY_TYPE}
							value={watch('companyType')}
							setValue={(value) => {
								setValue('companyType', value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={viewMode}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="codAvailable" isLoading={isLoading}>
						<SwitchInput className="mt-3" name="codAvailable" viewMode={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="codType" isLoading={isLoading}>
						<EnumSelect
							name="codType"
							enumName={ENUMS.COD_TYPE}
							value={watch('codType')}
							setValue={(value) => {
								setValue('codType', value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={viewMode}
							valueRemovable
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="codTransfer" isLoading={isLoading}>
						<StringInput name="codTransfer" disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="rating" isLoading={isLoading}>
						<IntegerInput name="rating" showArrows={false} disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="status" isLoading={isLoading}>
						<EnumSelect
							name="status"
							enumName={ENUMS.ACTIVE_PASSIVE}
							value={watch('status')}
							setValue={(value) => {
								setValue('status', value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={viewMode}
							valueRemovable
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="paymentTerm" isLoading={isLoading}>
						<StringInput name="paymentTerm" disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="maxCheckStatusLimit" isLoading={isLoading}>
						<IntegerInput name="maxCheckStatusLimit" showArrows={false} disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="maxCheckStatusRequestLimit" isLoading={isLoading}>
						<IntegerInput
							name="maxCheckStatusRequestLimit"
							showArrows={false}
							disabled={viewMode || watch('maxCheckStatusLimit') > 1}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="webhookAvailable" isLoading={isLoading}>
						<SwitchInput className="mt-3" name="webhookAvailable" viewMode={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="testConnectionAvailable" isLoading={isLoading}>
						<SwitchInput className="mt-3" name="testConnectionAvailable" viewMode={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="deliveryGatewayImplemented" isLoading={isLoading}>
						<SwitchInput className="mt-3" name="deliveryGatewayImplemented" viewMode={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="printAWBNA" isLoading={isLoading}>
						<SwitchInput className="mt-3" name="printAWBNA" viewMode={viewMode} />
					</FormItem>
					<FormItem label="trackingURL" isLoading={isLoading}>
						<StringInput name="trackingURL" disabled={viewMode} />
					</FormItem>
					<FormItem label="description" isLoading={isLoading}>
						<TextArea name="description" maxLength="4096" rows="8" disabled={viewMode} />
					</FormItem>
					<FormItem label="jsonSettings" isLoading={isLoading}>
						<TextArea name="jsonSettings" maxLength="4096" rows="8" disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="countries" isLoading={isLoading}>
						<EnumSelect
							name="countries"
							enumName={ENUMS.COUNTRIES}
							value={watch('countries')}
							setValue={(value) => {
								setValue('countries', value || [], { shouldValidate: true, shouldDirty: true });
							}}
							disabled={viewMode}
							multipleSelection
							valueRemovable
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="serviceTypes" isLoading={isLoading}>
						<EnumSelect
							name="serviceTypes"
							enumName={ENUMS.DC_SERVICE_TYPES}
							value={watch('serviceTypes')}
							setValue={(value) => {
								setValue('serviceTypes', value || [], { shouldValidate: true, shouldDirty: true });
							}}
							disabled={viewMode}
							multipleSelection
							valueRemovable
						/>
					</FormItem>
					<FormItem>
						<Typography variant="p2" fontWeight="500">
							{t('deliveryCompanySettingsConfig')}
							<Tooltip placement="right" title={'deliveryCompanySettingsConfigTooltip'}>
								<Icon icon={faCircleInfo} />
							</Tooltip>
						</Typography>
						<FormItem>
							<Table
								columns={deliveryCompanySettingsConfigColumns}
								data={deliveryCompanySettingsConfigControlledFields}
								loading={isLoading}
								onRowDelete={(item) => removeDeliveryCompanySettingsConfigItem(item, ID)}
								deletableLastRow
								showAddNewRowButton={mode !== 'view'}
								onRowAdd={handleAddDeliveryCompanySettings}
							/>
						</FormItem>
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />

				{mode !== 'view' && (
					<Button
						loading={isLoading}
						label={mode === 'edit' ? 'save' : 'button.add'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default DeliveryCompanyForm;
